import React, { useState } from 'react';
import {
  Card, Button, Table, Form, Input, Select, DatePicker, Tag, Space,
  Typography, Modal, Descriptions, Popover, Upload, Image, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const STATUS_MAP = {
  pending: { text: '待审核', color: 'default' },
  rejected: { text: '审核拒绝', color: 'error' },
  approved: { text: '待打款', color: 'warning' },
  paid: { text: '已打款', color: 'success' },
};

const ENTERPRISES = [
  { value: 'ENT001', label: '东莞康林物联网有限公司' },
  { value: 'ENT002', label: '深圳汇云科技有限公司' },
  { value: 'ENT003', label: '杭州智联设备有限公司' },
];

const MOCK_DATA = [
  { key: '1', withdrawNo: 'WD20260410001', entName: '东莞康林物联网有限公司', entId: 'ENT001', applyTime: '2026-04-10 14:00:00', bills: [{ billNo: 'BILL202604-001', period: '2026-03', amount: 13464.00 }], amount: 13464.00, auditTime: null, status: 'pending', payTime: null, bank: { accountName: '东莞康林物联网有限公司', bankName: '中国建设银行', bankCode: '105602001052', accountNo: '4420 1562 3000 5271 8888' } },
  { key: '2', withdrawNo: 'WD20260405002', entName: '东莞康林物联网有限公司', entId: 'ENT001', applyTime: '2026-04-05 10:00:00', bills: [{ billNo: 'BILL202603-002', period: '2026-02', amount: 10676.92 }, { billNo: 'BILL202603-004', period: '2026-02', amount: 5359.50 }], amount: 16036.42, auditTime: '2026-04-06 09:00:00', status: 'paid', payTime: '2026-04-08 16:30:00', bank: { accountName: '东莞康林物联网有限公司', bankName: '中国建设银行', bankCode: '105602001052', accountNo: '4420 1562 3000 5271 8888' }, voucher: 'https://placehold.co/600x400?text=结算凭证' },
  { key: '3', withdrawNo: 'WD20260401003', entName: '深圳汇云科技有限公司', entId: 'ENT002', applyTime: '2026-04-01 09:00:00', bills: [{ billNo: 'BILL202603-002', period: '2026-02', amount: 10676.92 }], amount: 10676.92, auditTime: '2026-04-02 10:00:00', status: 'rejected', rejectReason: '发票信息有误，请重新提交', payTime: null, bank: { accountName: '深圳汇云科技有限公司', bankName: '招商银行', bankCode: '308584000013', accountNo: '7559 2816 0410 001' } },
  { key: '4', withdrawNo: 'WD20260320004', entName: '杭州智联设备有限公司', entId: 'ENT003', applyTime: '2026-03-20 11:00:00', bills: [{ billNo: 'BILL202603-004', period: '2026-02', amount: 5359.50 }], amount: 5359.50, auditTime: '2026-03-21 14:00:00', status: 'approved', payTime: null, bank: { accountName: '杭州智联设备有限公司', bankName: '工商银行', bankCode: '102331000019', accountNo: '1202 0236 0900 1234 567' } },
];

const WithdrawAudit = () => {
  const [form] = Form.useForm();
  const [billModalVisible, setBillModalVisible] = useState(false);
  const [billModalData, setBillModalData] = useState([]);
  const [bankModalVisible, setBankModalVisible] = useState(false);
  const [bankModalData, setBankModalData] = useState(null);
  const [invoiceVisible, setInvoiceVisible] = useState(false);
  const [voucherVisible, setVoucherVisible] = useState(false);
  const [voucherUrl, setVoucherUrl] = useState('');
  // 审核
  const [auditVisible, setAuditVisible] = useState(false);
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditResult, setAuditResult] = useState('approve');
  const [rejectReason, setRejectReason] = useState('');
  // 打款
  const [payVisible, setPayVisible] = useState(false);
  const [payRecord, setPayRecord] = useState(null);

  const billColumns = [
    { title: '账单号', dataIndex: 'billNo', width: 180 },
    { title: '对应账期', dataIndex: 'period', width: 120 },
    { title: '账单金额(CNY)', dataIndex: 'amount', width: 140, align: 'right', render: (v) => `¥ ${v.toFixed(2)}` },
  ];

  const columns = [
    { title: '提现订单号', dataIndex: 'withdrawNo', width: 180 },
    {
      title: '申请企业', key: 'ent', width: 180,
      render: (_, r) => (<div><div style={{ fontWeight: 500 }}>{r.entName}</div><Text type="secondary" style={{ fontSize: 12 }}>{r.entId}</Text></div>),
    },
    { title: '申请时间', dataIndex: 'applyTime', width: 170 },
    {
      title: '对应账单', key: 'bills', width: 90,
      render: (_, r) => <Button type="link" size="small" onClick={() => { setBillModalData(r.bills); setBillModalVisible(true); }}>{r.bills.length}笔</Button>,
    },
    { title: '提现金额', dataIndex: 'amount', width: 130, align: 'right', render: (v) => <Text strong>¥ {v.toFixed(2)}</Text> },
    { title: '审核时间', dataIndex: 'auditTime', width: 170, render: (v) => v || null },
    {
      title: '提现状态', dataIndex: 'status', width: 120,
      render: (v, r) => {
        const s = STATUS_MAP[v];
        if (v === 'rejected') return <Popover content={r.rejectReason} title="驳回原因"><Tag color={s.color} style={{ cursor: 'pointer' }}>{s.text}</Tag></Popover>;
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    { title: '打款时间', dataIndex: 'payTime', width: 170, render: (v) => v || null },
    {
      title: '发票信息', key: 'invoice', width: 90,
      render: () => <Button type="link" size="small" onClick={() => setInvoiceVisible(true)}>查看</Button>,
    },
    {
      title: '提现账户', key: 'bank', width: 90,
      render: (_, r) => <Button type="link" size="small" onClick={() => { setBankModalData(r.bank); setBankModalVisible(true); }}>查看</Button>,
    },
    {
      title: '结算凭证', key: 'voucher', width: 90,
      render: (_, r) => r.voucher ? <Button type="link" size="small" onClick={() => { setVoucherUrl(r.voucher); setVoucherVisible(true); }}>查看</Button> : null,
    },
    {
      title: '操作', key: 'action', width: 120, fixed: 'right',
      render: (_, r) => {
        if (r.status === 'pending') return <Button type="link" size="small" onClick={() => { setAuditRecord(r); setAuditResult('approve'); setRejectReason(''); setAuditVisible(true); }}>审核</Button>;
        if (r.status === 'approved') return <Button type="link" size="small" danger onClick={() => { setPayRecord(r); setPayVisible(true); }}>打款</Button>;
        return null;
      },
    },
  ];

  const handleAudit = () => {
    if (auditResult === 'reject' && !rejectReason.trim()) { message.warning('请输入拒绝原因'); return; }
    message.success(auditResult === 'approve' ? '审核通过，请尽快打款并上传结算凭证' : '已拒绝');
    setAuditVisible(false);
  };

  const handlePay = () => {
    message.success('打款成功');
    setPayVisible(false);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}><Title level={4}>提现审核</Title></div>

      {/* 查询条件 */}
        <Form form={form} layout="inline" style={{ flexWrap: 'wrap', gap: '8px 0', marginBottom: 16 }}>
          <Form.Item style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="entType" initialValue="name" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="name">企业名称</Option>
                  <Option value="id">企业ID</Option>
                </Select>
              </Form.Item>
              <Form.Item shouldUpdate={(p, c) => p.entType !== c.entType} noStyle>
                {({ getFieldValue }) => getFieldValue('entType') === 'id'
                  ? <Form.Item name="entKeyword" noStyle><Input placeholder="企业ID" allowClear style={{ width: 200 }} /></Form.Item>
                  : <Form.Item name="entKeyword" noStyle><Select placeholder="请选择企业" allowClear showSearch optionFilterProp="label" style={{ width: 200 }} options={ENTERPRISES} /></Form.Item>
                }
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="withdrawNo" style={{ marginRight: 8 }}>
            <Input placeholder="提现编号" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status" style={{ marginRight: 8 }}>
            <Select placeholder="状态" allowClear style={{ width: 130 }}>
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" style={{ marginRight: 8 }}>
            <Space.Compact>
              <Form.Item name="timeType" initialValue="apply" noStyle>
                <Select style={{ width: 100 }}>
                  <Option value="apply">申请时间</Option>
                  <Option value="audit">审核时间</Option>
                  <Option value="pay">打款时间</Option>
                </Select>
              </Form.Item>
              <RangePicker style={{ width: 260 }} placeholder={['开始时间', '结束时间']} />
            </Space.Compact>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

      <Card bordered={false}>
        <Table columns={columns} dataSource={MOCK_DATA} rowKey="key" scroll={{ x: 'max-content' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} style={{ whiteSpace: 'nowrap' }} />
      </Card>

      {/* 对应账单 */}
      <Modal title="对应账单" open={billModalVisible} onCancel={() => setBillModalVisible(false)} footer={null} width={550}>
        <Table columns={billColumns} dataSource={billModalData} rowKey="billNo" pagination={false} size="small" />
      </Modal>

      {/* 提现账户 */}
      <Modal title="提现账户" open={bankModalVisible} onCancel={() => setBankModalVisible(false)} footer={null}>
        {bankModalData && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="账户名">{bankModalData.accountName}</Descriptions.Item>
            <Descriptions.Item label="开户银行">{bankModalData.bankName}</Descriptions.Item>
            <Descriptions.Item label="开户行号">{bankModalData.bankCode}</Descriptions.Item>
            <Descriptions.Item label="银行账号">{bankModalData.accountNo}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 发票信息 */}
      <Modal title="发票信息" open={invoiceVisible} onCancel={() => setInvoiceVisible(false)} footer={null} centered>
        <div style={{ textAlign: 'center' }}><Image src="https://placehold.co/600x400?text=发票附件" style={{ maxWidth: '100%' }} /></div>
      </Modal>

      {/* 结算凭证 */}
      <Modal title="结算凭证" open={voucherVisible} onCancel={() => setVoucherVisible(false)} footer={null} centered>
        <div style={{ textAlign: 'center' }}><Image src={voucherUrl} style={{ maxWidth: '100%' }} /></div>
      </Modal>

      {/* 审核弹窗 */}
      <Modal title="提现审核" open={auditVisible} onCancel={() => setAuditVisible(false)} onOk={handleAudit} okText="确认" cancelText="取消" destroyOnClose>
        {auditRecord && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="提现编号">{auditRecord.withdrawNo}</Descriptions.Item>
              <Descriptions.Item label="申请企业">{auditRecord.entName}</Descriptions.Item>
              <Descriptions.Item label="提现金额">¥ {auditRecord.amount.toFixed(2)}</Descriptions.Item>
            </Descriptions>
            <Form layout="vertical">
              <Form.Item label="审核结果" required>
                <Select value={auditResult} onChange={setAuditResult} style={{ width: '100%' }}>
                  <Option value="approve">审核通过</Option>
                  <Option value="reject">审核拒绝</Option>
                </Select>
              </Form.Item>
              {auditResult === 'approve' && (
                <div style={{ padding: 12, background: '#e6f7ff', borderRadius: 6, fontSize: 13 }}>
                  审核通过后，请尽快完成打款并上传结算凭证。
                </div>
              )}
              {auditResult === 'reject' && (
                <Form.Item label="拒绝原因" required>
                  <TextArea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="请输入拒绝原因" />
                </Form.Item>
              )}
            </Form>
          </>
        )}
      </Modal>

      {/* 打款弹窗 */}
      <Modal title="确认打款" open={payVisible} onCancel={() => setPayVisible(false)} onOk={handlePay} okText="确认打款" cancelText="取消" destroyOnClose>
        {payRecord && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="提现编号">{payRecord.withdrawNo}</Descriptions.Item>
              <Descriptions.Item label="打款金额">¥ {payRecord.amount.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="收款账户">{payRecord.bank.accountName} / {payRecord.bank.accountNo}</Descriptions.Item>
            </Descriptions>
            <Form layout="vertical">
              <Form.Item label="上传结算凭证" required>
                <Upload maxCount={3} beforeUpload={() => false} accept=".jpg,.jpeg,.png,.pdf" listType="picture-card">
                  <div><UploadOutlined /><div style={{ marginTop: 8 }}>上传</div></div>
                </Upload>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default WithdrawAudit;
